import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Pagination,
  Skeleton,
  IconButton,
  Checkbox,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/**
 * MobileCardList — Mobile-friendly card-based list view
 * Replaces DataGrid on mobile devices.
 * 
 * Props:
 * - rows: Array of data rows
 * - loading: boolean
 * - onRowClick: (row) => void — navigate to detail
 * - renderCard: (row) => ReactNode — custom card template (optional)
 * - primaryField: string — field name for card title
 * - secondaryField: string — field name for card subtitle
 * - chipField: { field, label?, colorMap? } — field to show as chip
 * - statusField: { field, label?, colorMap? } — field for status badge
 * - extraFields: [{ field, label, render? }] — additional info lines
 * - actions: (row) => ReactNode — extra action buttons
 * - pagination: { page, pageSize, total, onPageChange }
 * - selectable: boolean — show checkboxes
 * - selectedIds: Set — selected row IDs
 * - onSelectionChange: (id, checked) => void
 * - idField: string (default: 'id')
 * - emptyMessage: string
 */
const MobileCardList = ({
  rows = [],
  loading = false,
  onRowClick,
  renderCard,
  primaryField,
  secondaryField,
  chipField,
  statusField,
  extraFields = [],
  actions,
  pagination,
  selectable = false,
  selectedIds = new Set(),
  onSelectionChange,
  idField = 'id',
  emptyMessage = 'Nessun risultato trovato',
}) => {
  
  if (loading) {
    return (
      <Stack spacing={1.5} sx={{ p: 1 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Card key={i} variant="outlined">
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={20} />
              <Skeleton variant="text" width="80%" height={18} />
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  if (rows.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const getStatusColor = (value, colorMap) => {
    if (colorMap && colorMap[value]) return colorMap[value];
    // Default color mapping for common statuses
    const v = String(value).toLowerCase();
    if (['attivo', 'pagata', 'pagato', 'completato', 'consegnato', 'true', '1', 'si', 'sì'].includes(v)) return 'success';
    if (['scaduta', 'scaduto', 'errore', 'false', '0', 'no', 'inattivo', 'disattivo'].includes(v)) return 'error';
    if (['in corso', 'parziale', 'pending', 'in attesa', 'bozza'].includes(v)) return 'warning';
    return 'default';
  };

  const getNestedValue = (obj, path) => {
    if (!path) return undefined;
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  return (
    <Box>
      <Stack spacing={1} sx={{ p: 0.5 }}>
        {rows.map((row) => {
          const id = getNestedValue(row, idField);
          
          // If custom renderCard is provided, use it
          if (renderCard) {
            return (
              <Box key={id}>
                {renderCard(row)}
              </Box>
            );
          }

          const cardContent = (
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Primary field - Title */}
                  {primaryField && (
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {getNestedValue(row, primaryField) || '—'}
                    </Typography>
                  )}
                  
                  {/* Secondary field - Subtitle */}
                  {secondaryField && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {getNestedValue(row, secondaryField) || '—'}
                    </Typography>
                  )}
                </Box>

                {/* Status badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1, flexShrink: 0 }}>
                  {statusField && (
                    <Chip
                      label={getNestedValue(row, statusField.field) != null 
                        ? (statusField.labelMap 
                          ? statusField.labelMap[getNestedValue(row, statusField.field)] 
                          : String(getNestedValue(row, statusField.field)))
                        : '—'
                      }
                      size="small"
                      color={getStatusColor(getNestedValue(row, statusField.field), statusField.colorMap)}
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  )}
                  {onRowClick && !selectable && (
                    <ChevronRightIcon color="action" sx={{ fontSize: 20 }} />
                  )}
                </Box>
              </Box>

              {/* Extra fields */}
              {extraFields.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  {extraFields.map((ef) => {
                    const val = ef.render ? ef.render(row) : getNestedValue(row, ef.field);
                    if (val == null || val === '') return null;
                    return (
                      <Typography key={ef.field} variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        {ef.label && <span style={{ fontWeight: 500 }}>{ef.label}:</span>}
                        {typeof val === 'string' || typeof val === 'number' ? val : val}
                      </Typography>
                    );
                  })}
                </Stack>
              )}

              {/* Chip field */}
              {chipField && getNestedValue(row, chipField.field) && (
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getNestedValue(row, chipField.field)}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </Box>
              )}
            </CardContent>
          );

          return (
            <Card
              key={id}
              variant="outlined"
              sx={{
                position: 'relative',
                ...(selectedIds.has(id) && {
                  borderColor: 'primary.main',
                  bgcolor: 'primary.50',
                }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
                {selectable && (
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(id)}
                      onChange={(e) => onSelectionChange && onSelectionChange(id, e.target.checked)}
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {onRowClick ? (
                    <CardActionArea onClick={() => onRowClick(row)}>
                      {cardContent}
                    </CardActionArea>
                  ) : (
                    cardContent
                  )}
                </Box>
                {actions && (
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5 }}>
                    {actions(row)}
                  </Box>
                )}
              </Box>
            </Card>
          );
        })}
      </Stack>

      {/* Pagination */}
      {pagination && pagination.total > pagination.pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Pagination
            count={Math.ceil(pagination.total / pagination.pageSize)}
            page={pagination.page + 1}
            onChange={(e, page) => pagination.onPageChange(page - 1)}
            size="small"
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileCardList;
